import { isObject, uniqBy } from "lodash";
import { InsightFormFields } from "pages/RS-Workspace/components/Scenario/components/InsightDialog";
import { CutType, GlobalEntityType, RsWorkspaceEntityType, TimestampType, } from "shared/types";
import { getNumberFromNumberString, renderNumberValue } from "utils/string";
import { isNotEmpty } from "utils/validate";
import { InsightCutOperations, InsightInputType, InsightOutputTypes, InsightScoreErrorType, OutputWeight, ResultSetElementType, InsightOutputVariableTypes, } from "../rs-workspace.types";
import { defaultInputSetInput, getDefaultTimeFrame } from "./scenario.utils";
import { getFieldRestrictions } from "store/knowledge-base-item/knowledge-base-item.utils";
import { findDictionaryItemByValue, getDefaultUnitsPrefix, pastFormEventValueTypes, recipeParameterUnitsType, searchDirections, timeUnits, } from "shared/rsDictionary";
import { getSelectedProcessVariable } from "store/global/global.utils";
import { getResultElementsUsingInsight, getSimulationVariantsOptions } from "./results.utils";
import { DELETE_SCENARIO_ENTITY_MESSAGES } from "shared/labels";
import { CustomVariableValueStatus, getCustomVariableStatus, parseComplexUnitToHtml, } from "store/recipe/custom-variables.utils";
import { flatSteps, getRecipeTimestamps } from "store/recipe/recipe.utils";
import { EventValueType } from "store/recipe/recipe.types";
import { isInsightInputSetCompleted, isSimulationVariantCompleted } from "./completeness.utils";
import { OUTPUT_FINAL_ID } from "../selectors/rs-workspace.selectors";
export var InsightDialogTestIds;
(function (InsightDialogTestIds) {
    InsightDialogTestIds["InputSet"] = "input-set";
    InsightDialogTestIds["InputSetInput"] = "input-set-input";
    InsightDialogTestIds["InputsPerSet"] = "inputs-per-set";
    InsightDialogTestIds["InputsPerSetButton"] = "inputs-per-set-button";
    InsightDialogTestIds["AddInputSetButton"] = "add-input-set";
    InsightDialogTestIds["ScoreIcon"] = "score-icon";
    InsightDialogTestIds["FinalScoreIcon"] = "final-score-icon";
    InsightDialogTestIds["EvaluationSource"] = "evaluation-source";
})(InsightDialogTestIds || (InsightDialogTestIds = {}));
export const mapOutputWeightsToValues = {
    [OutputWeight.Low]: 0.5,
    [OutputWeight.Normal]: 1,
    [OutputWeight.High]: 2,
    [OutputWeight.VeryHigh]: 4,
    [OutputWeight.Unset]: 0,
    [OutputWeight.Essential]: "*",
};
export const getDefaultOutput = (restrictions) => ({
    id: null,
    sourceA: null,
    sourceB: null,
    transformationOperation: null,
    type: null,
    weight: OutputWeight.Normal,
    cutOperation: InsightCutOperations.Trim,
    advancedEnabled: false,
    advanced: {
        robustness: 0,
        reliability: 0,
    },
    operation: null,
    ...(restrictions
        ? { timeFrame: getDefaultTimeFrame(getFieldRestrictions(restrictions, [InsightOutputTypes.Evaluation])) }
        : {}),
    processVariable: null,
});
export const getInsightInputSetOptions = (insightId, insightInputSetId, workspaceInsights, processVariables, incompleteSimulationIds, simulations, cellModels, recipes) => {
    const currentInsight = workspaceInsights.find(({ id }) => id === insightId);
    if (!currentInsight) {
        return [];
    }
    else {
        return currentInsight.state.input.inputSets.reduce((acc, inputSet) => {
            const isCompleted = isInsightInputSetCompleted(inputSet, currentInsight.state.outputs, processVariables, incompleteSimulationIds, simulations, cellModels, recipes);
            const { id } = inputSet;
            if (isCompleted || id === insightInputSetId) {
                acc.push({
                    value: id,
                    label: `Input set ${id}`,
                    disabled: !isCompleted && id === insightInputSetId,
                });
            }
            return acc;
        }, []);
    }
};
export const getInsightInputVariantsOptions = (inputType, simulations, cellModels, recipes, simulationId, selectedVariantId) => {
    if (inputType === InsightInputType.PROCESS_DATA)
        return [];
    const allVariantOptions = getSimulationVariantsOptions(simulations, simulationId);
    const simulation = simulations.find(({ id }) => id === simulationId);
    return allVariantOptions.reduce((acc, option) => {
        const variant = simulation?.state?.adjustments?.find(({ id }) => id === option.value);
        if (isSimulationVariantCompleted(simulation, cellModels, recipes, variant) ||
            option.value === selectedVariantId) {
            return [
                ...acc,
                { ...option, disabled: !isSimulationVariantCompleted(simulation, cellModels, recipes, variant) },
            ];
        }
        return acc;
    }, []);
};
export const getInsightOutputOptions = (insightId, insightInputSetId, outputId, workspaceInsights, persistedInsightsData, processVariables, recipes) => {
    const currentInsight = workspaceInsights.find(({ id }) => id === insightId);
    if (!currentInsight || !insightId || !insightInputSetId) {
        return [];
    }
    else {
        const outputsOptions = currentInsight.state.outputs.reduce((acc, output) => {
            const isCompleted = output.type !== null && checkBasicScoreCalculationConditions(output, processVariables, recipes);
            const { id, type } = output;
            if (id !== null && (isCompleted || id === outputId)) {
                acc.push({
                    value: id,
                    label: `${type} ${id}`,
                    disabled: !isCompleted && id === outputId,
                });
            }
            return acc;
        }, []);
        const finalScore = calculateInputSetFinalScore(insightId, insightInputSetId, persistedInsightsData);
        const hasFinalScoreOption = isNotEmpty(finalScore);
        return [
            ...(hasFinalScoreOption || outputId === OUTPUT_FINAL_ID
                ? [
                    {
                        value: OUTPUT_FINAL_ID,
                        label: "Final Score",
                        disabled: outputId === OUTPUT_FINAL_ID && !hasFinalScoreOption,
                    },
                ]
                : []),
            ...outputsOptions,
        ];
    }
};
export const getInsightSourcesIds = (outputs) => outputs.reduce((acc, outputItem) => {
    const sourceAId = outputItem.sourceA === null ? null : Number(outputItem.sourceA?.value);
    const sourceBId = outputItem.sourceB === null ? null : Number(outputItem.sourceB?.value);
    if (outputItem.type === InsightOutputTypes.Comparison) {
        return [...acc, sourceAId, sourceBId];
    }
    return [...acc, sourceAId];
}, []);
export const getInsightLatestScoreData = (persistedInsightsData, insightId) => persistedInsightsData.find((item) => !item.isOriginal && item.insightId === (insightId ?? -1));
export const getUpdatedPreviewsDataAfterDeletingSimulationComponent = (simulations, insights, optimizations, optimizationPreviews, persistedInsightsData, entityType, removedEntityId) => {
    const optimizationsUsingDeletedScore = [];
    const relatedSimulations = simulations.filter(({ id, cellModelId, recipeId }) => {
        switch (entityType) {
            case GlobalEntityType.Recipe:
                return recipeId === removedEntityId;
            case GlobalEntityType.CellModel:
                return cellModelId === removedEntityId;
            case RsWorkspaceEntityType.Simulation:
            default:
                return id === removedEntityId;
        }
    });
    const relatedInsights = insights.filter((insight) => {
        const simulationUsedInInsight = insight.state.input.inputSets.some((inputSet) => {
            return inputSet.inputs.some((input) => {
                const hasMatchInRelatedSimulations = relatedSimulations.some((simulation) => simulation.id === input.inputId);
                return input.inputType === InsightInputType.SIMULATION && hasMatchInRelatedSimulations;
            });
        });
        return simulationUsedInInsight;
    });
    const untouchedPersistedData = persistedInsightsData.filter((item) => !relatedInsights.find((insight) => insight.id === item.insightId));
    const relatedPersistedData = persistedInsightsData.filter((item) => !!relatedInsights.find((insight) => insight.id === item.insightId));
    const updatedPersistedData = relatedPersistedData.map(({ scores, insightId, ...rest }) => {
        const insight = relatedInsights.find((item) => item.id === insightId);
        const updatedScores = scores.filter(({ inputSetId }) => {
            const insightTargetInputSet = insight?.state.input.inputSets.find(({ id }) => id === inputSetId);
            const inputSetUsingDeletedEntity = !!insightTargetInputSet &&
                insightTargetInputSet?.inputs.some((input) => {
                    const hasMatchInRelatedSimulations = relatedSimulations.some((simulation) => simulation.id === input.inputId);
                    return input.inputType === InsightInputType.SIMULATION && hasMatchInRelatedSimulations;
                });
            const optimizationUsingInsightInputSetWithDeletedScore = inputSetUsingDeletedEntity &&
                optimizations.find(({ state }) => state.input?.insightId === insight?.id && state.input?.insightInputSetId === inputSetId);
            if (!!optimizationUsingInsightInputSetWithDeletedScore &&
                !optimizationsUsingDeletedScore.includes(optimizationUsingInsightInputSetWithDeletedScore)) {
                optimizationsUsingDeletedScore.push(optimizationUsingInsightInputSetWithDeletedScore);
            }
            return !inputSetUsingDeletedEntity;
        });
        return { ...rest, insightId, scores: updatedScores };
    });
    const updatedPersistedOptimizationPreviews = Object.keys(optimizationPreviews.persisted).reduce((acc, key) => {
        if (!optimizationsUsingDeletedScore.find((item) => item.id === Number(key))) {
            return { ...acc, [key]: optimizationPreviews.persisted[Number(key)] };
        }
        return acc;
    }, {});
    return {
        updatedScoresData: [...untouchedPersistedData, ...updatedPersistedData],
        updatedPersistedOptimizationPreviews,
    };
};
export const checkScoreHasBiasedValues = (score) => Boolean(score?.data?.graphResult?.insightGraph?.yValues?.some((value) => typeof value === "string"));
export const getInsightNodeScoresData = (persistedInsightsData, inputSets, outputs, insightId, previewRequests, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes) => {
    const insightScoreData = getInsightLatestScoreData(persistedInsightsData, insightId);
    const filledOutputs = outputs?.filter((item) => item.type !== null);
    const someScorePending = inputSets.some((inputSetItem) => previewRequests.some(({ data }) => data.insightId === insightId && data.inputSetId === inputSetItem.id));
    return inputSets?.map((inputSetItem) => {
        const isInputSetComplete = isInsightInputSetCompleted(inputSetItem, outputs, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes);
        const inputSetScores = insightScoreData?.scores.filter((score) => score.inputSetId === inputSetItem.id);
        const calculatedInputSetScores = insightScoreData?.scores.filter((item) => item.inputSetId === inputSetItem.id && !checkScoreErrored(item.score));
        const finalScore = calculateInputSetFinalScore(insightId, inputSetItem.id, persistedInsightsData);
        const allScoresForOutputsCalculated = calculatedInputSetScores?.length === filledOutputs?.length;
        const outputScores = filledOutputs?.map((output) => {
            const outputScoreEntity = inputSetScores?.find((score) => score.outputId === output.id);
            const scorePending = getInsightPreviewOperationPending(previewRequests, insightId, inputSetItem.id, output.id ?? 0);
            return {
                ...prepareBaseScoreItem(outputScoreEntity?.score, checkCanDisplayScore(output, inputSetItem.inputs, processVariables, recipes)),
                pending: scorePending,
                highlighted: true,
                type: output.type,
                weight: output.weight,
            };
        });
        const allScoresAvailable = outputScores.every(({ score }) => isNotEmpty(score));
        return {
            outputScores,
            finalScore: filledOutputs?.length === 1
                ? outputScores?.[0]
                : {
                    score: allScoresForOutputsCalculated && allScoresAvailable ? finalScore : undefined,
                    errored: isInputSetComplete && inputSetScores?.some(({ score }) => checkScoreErrored(score)),
                    hasBiasedValue: inputSetScores?.some((item) => checkScoreHasBiasedValues(item.score)),
                    pending: someScorePending,
                    highlighted: true,
                },
        };
    });
};
export const getInsightUpdatedInputsPerSetFormData = (currentInputSets, currentInputsPerSetCount, newInputsPerSetCount, currentOutputs) => {
    const outputsToUpdateMeta = [];
    const allSourcesIds = getInsightSourcesIds(currentOutputs);
    const inputSetsData = currentInputSets.map((inputSet) => {
        const inputSetInputs = inputSet[InsightFormFields.inputs];
        if (newInputsPerSetCount > currentInputsPerSetCount) {
            const newInputsCount = newInputsPerSetCount - currentInputsPerSetCount;
            const nextInputId = currentInputsPerSetCount + 1;
            return {
                [InsightFormFields.id]: inputSet.id,
                [InsightFormFields.inputs]: [
                    ...inputSetInputs,
                    ...Array(newInputsCount)
                        .fill(defaultInputSetInput)
                        .map((item, index) => ({
                        ...item,
                        [InsightFormFields.id]: nextInputId + index,
                    })),
                ],
            };
        }
        const inputsUsedAsSources = inputSetInputs.filter((item) => allSourcesIds.includes(item.id));
        const inputsAvailableForRemoval = inputSetInputs.filter((item) => !allSourcesIds.includes(item.id));
        const updatedInputs = [...inputsUsedAsSources];
        inputsAvailableForRemoval.forEach((item) => {
            const reachedNewInputsCount = updatedInputs.length === newInputsPerSetCount;
            const SMELLY_EDGE_CASE_CONDITION = item.id === 2 && newInputsPerSetCount === 2;
            if (item.inputId === null && !reachedNewInputsCount) {
                if (SMELLY_EDGE_CASE_CONDITION) {
                    /**
                     * covers the case when we switch from 3 to 2 inputs and the first one is selected -
                     * - as far as we put inputs from the end, we end up with the first input placed at the end.
                     * Thankfully we can have only 3 inputs max, so this case is a loner
                     */
                    updatedInputs.push(item);
                }
                else {
                    updatedInputs.unshift(item);
                }
            }
            if (item.inputId !== null && !reachedNewInputsCount) {
                updatedInputs.unshift(item);
            }
        });
        const inputsData = updatedInputs.reduce((acc, inputItem, index) => {
            const updatedInputId = index + 1;
            const outputsWithSourceFromCurrentInput = currentOutputs.filter((output) => (!!output.sourceA && output.sourceA.value === inputItem.id) ||
                (!!output.sourceB && output.sourceB.value === inputItem.id));
            outputsWithSourceFromCurrentInput.forEach((output) => {
                outputsToUpdateMeta.push({
                    outputId: output?.id ?? 0,
                    updatedInputId,
                    isSourceB: inputItem.id === output.sourceB?.value,
                });
            });
            return [...acc, { ...inputItem, id: updatedInputId }];
        }, []);
        return {
            [InsightFormFields.id]: inputSet.id,
            [InsightFormFields.inputs]: allSourcesIds.some((item) => item !== null)
                ? inputsData
                : inputSetInputs.slice(0, newInputsPerSetCount),
        };
    });
    const outputsData = currentOutputs.map((item) => {
        const updatedSourceAData = outputsToUpdateMeta.find(({ outputId, isSourceB }) => outputId === item.id && !isSourceB);
        const updatedSourceBData = outputsToUpdateMeta.find(({ outputId, isSourceB }) => outputId === item.id && isSourceB);
        return {
            ...item,
            sourceA: updatedSourceAData
                ? {
                    value: updatedSourceAData?.updatedInputId,
                    label: `Input ${updatedSourceAData?.updatedInputId}`,
                }
                : item.sourceA,
            ...(item.type === InsightOutputTypes.Comparison
                ? {
                    sourceB: updatedSourceBData
                        ? {
                            value: updatedSourceBData?.updatedInputId,
                            label: `Input ${updatedSourceBData?.updatedInputId}`,
                        }
                        : item.sourceB,
                }
                : {}),
        };
    });
    return {
        inputSetsData,
        outputsData: outputsData.sort((itemA, itemB) => (itemA.id ?? 0) - (itemB.id ?? 0)),
    };
};
export const getFormattedOutputFormData = (outputFormData) => {
    const { sourceA, sourceB, processVariable, operation, transformationOperation, advanced, ...restOutput } = outputFormData;
    const processVariablePayload = isNotEmpty(processVariable, true)
        ? {
            [InsightFormFields.type]: processVariable?.type,
            ...(typeof processVariable?.value === "string"
                ? { [InsightFormFields.variableType]: processVariable?.value }
                : {
                    [InsightFormFields.customVariableId]: processVariable?.value,
                    [InsightFormFields.recipeId]: processVariable?.recipeId,
                }),
            [InsightFormFields.units]: processVariable?.units,
        }
        : null;
    const operationPayload = isNotEmpty(operation, true)
        ? {
            [InsightFormFields.name]: operation[InsightFormFields.type]?.label,
            [InsightFormFields.type]: operation[InsightFormFields.type]?.value,
            [InsightFormFields.parameters]: operation[InsightFormFields.parameters].map((item) => ({
                ...item,
                value: getNumberFromNumberString(item.value, false),
            })),
            ...(operation[InsightFormFields.preProcessingEnabled]
                ? {
                    [InsightFormFields.preProcessingEnabled]: operation[InsightFormFields.preProcessingEnabled],
                    [InsightFormFields.preProcessingType]: operation[InsightFormFields.preProcessingType],
                }
                : {}),
            ...(operation[InsightFormFields.timeWindow]
                ? {
                    [InsightFormFields.timeWindow]: {
                        [InsightFormFields.value]: getNumberFromNumberString(operation[InsightFormFields.timeWindow].value, false),
                        [InsightFormFields.units]: operation[InsightFormFields.timeWindow].units?.value,
                    },
                }
                : {}),
        }
        : null;
    const transformationPayload = isNotEmpty(transformationOperation, true)
        ? {
            [InsightFormFields.name]: transformationOperation[InsightFormFields.operationType]?.label,
            [InsightFormFields.operationType]: transformationOperation[InsightFormFields.operationType]?.value,
            parameter: null,
        }
        : null;
    return {
        ...restOutput,
        [InsightFormFields.advanced]: {
            [InsightFormFields.robustness]: getNumberFromNumberString(advanced?.robustness, false),
            [InsightFormFields.reliability]: getNumberFromNumberString(advanced?.reliability, false),
        },
        [InsightFormFields.processVariable]: processVariablePayload,
        [InsightFormFields.sourceA]: !isNotEmpty(sourceA) ? sourceA : sourceA.value,
        [InsightFormFields.sourceB]: !isNotEmpty(sourceB) ? sourceB : sourceB.value,
        [InsightFormFields.operation]: operationPayload,
        [InsightFormFields.transformationOperation]: transformationPayload,
    };
};
export const getInsightDialogSubmitPayload = (values, workspaceId, insightDefaultName) => {
    const inputSets = values[InsightFormFields.inputSets].map((item) => {
        const inputs = item?.[InsightFormFields.inputs].map(({ id, inputId, inputType, ...restInput }) => ({
            ...restInput,
            [InsightFormFields.inputType]: isObject(inputId) ? inputId.groupId : inputType,
            [InsightFormFields.id]: id,
            [InsightFormFields.inputId]: inputId === null ? inputId : inputId.value,
        }));
        return {
            [InsightFormFields.id]: item.id,
            [InsightFormFields.inputs]: inputs,
        };
    });
    const outputs = values[InsightFormFields.outputs]
        .filter((output) => output.type !== null)
        .map(getFormattedOutputFormData);
    return {
        workspaceId,
        name: values[InsightFormFields.name] ?? insightDefaultName,
        state: {
            input: {
                inputCount: values[InsightFormFields.inputCount],
                inputSets,
            },
            outputs,
        },
    };
};
export const getInsightPreviewOperationPending = (previewRequests, insightId, inputSetId, outputId) => previewRequests.some(({ data }) => data.insightId === insightId && data.outputId === outputId && data.inputSetId === inputSetId);
export const checkOutputCombinationIsEssential = (essentialCombinationsList, operationType, transformationType) => {
    if (!essentialCombinationsList) {
        return false;
    }
    const combinationItemsSelected = isNotEmpty(operationType) && isNotEmpty(transformationType);
    if (!combinationItemsSelected) {
        return false;
    }
    return (!!essentialCombinationsList[operationType] &&
        essentialCombinationsList[operationType].includes(transformationType));
};
export const getActualSourceName = (inputEntity, workspaceSimulations) => {
    if (isNotEmpty(inputEntity?.inputId)) {
        let title = inputEntity?.inputId?.label;
        if (inputEntity?.inputType === InsightInputType.SIMULATION) {
            const simulation = workspaceSimulations?.find(({ id }) => id === inputEntity?.inputId?.value);
            const variant = !!simulation && simulation?.state?.adjustments?.find(({ id }) => id === inputEntity?.adjustmentId);
            if (!!simulation && !!variant && !!variant?.id) {
                title = `${inputEntity?.inputId?.label}_${variant?.name}`;
            }
        }
        return title;
    }
    return "Not selected";
};
export const getOperationSummaryText = (descriptions, variable, operation, transformationOperation, isPointInTime, timeframeLabel) => {
    const transformationOperationValue = isPointInTime
        ? "PointInTime"
        : transformationOperation?.operationType?.value;
    if ([variable, operation, transformationOperationValue, descriptions].some((item) => !isNotEmpty(item))) {
        return "";
    }
    let description = "";
    const { type, parameters } = operation;
    const maxParameter = parameters.find((item) => item.parameterType === "Max");
    const minParameter = parameters.find((item) => item.parameterType === "Min");
    const mappedParameters = parameters.map(({ parameterType, value }) => ({
        parameterType: `[${parameterType.toLowerCase()}]`,
        value: renderNumberValue(value),
    }));
    const hasCorrectMinMaxParameters = isNotEmpty(maxParameter?.value) &&
        !isNaN(Number(maxParameter?.value)) &&
        isNotEmpty(minParameter?.value) &&
        !isNaN(Number(minParameter?.value));
    if (mappedParameters.some((item) => isNaN(Number(item.value)))) {
        return "";
    }
    mappedParameters.push({ parameterType: "[variable]", value: variable.label });
    description = descriptions?.[type.value]?.[transformationOperationValue];
    if (mappedParameters.some((item) => description?.includes(item.parameterType) && !isNotEmpty(item.value)) ||
        (description?.includes("[range]") && !hasCorrectMinMaxParameters)) {
        return "";
    }
    mappedParameters.forEach((parameter) => {
        description = description?.replaceAll(parameter.parameterType, parameter.value);
    });
    if (hasCorrectMinMaxParameters) {
        description = description?.replaceAll("[range]", `${renderNumberValue(minParameter?.value)}-${renderNumberValue(maxParameter?.value)}`);
    }
    if (isPointInTime) {
        description = description?.replaceAll("[time]", timeframeLabel ?? "");
    }
    return description;
};
export const calculateInputSetFinalScore = (insightId, inputSetId, insightsData) => {
    const insightScoresData = getInsightLatestScoreData(insightsData, insightId);
    const insightScores = insightScoresData?.scores ?? [];
    const inputSetScores = insightScores.filter((item) => item.inputSetId === inputSetId);
    if (inputSetScores.length === 0 || insightScores.length === 0 || !insightScoresData) {
        return;
    }
    const noEssentialWeight = !inputSetScores.some((item) => item.weight === OutputWeight.Essential);
    const scoresWithEssentialWeight = inputSetScores.filter((item) => item.weight === OutputWeight.Essential);
    const scoresWithoutEssentialWeight = inputSetScores.filter((item) => item.weight !== OutputWeight.Essential);
    const getDivided = (scores) => scores.reduce((acc, scoreItem) => {
        const weight = mapOutputWeightsToValues[scoreItem.weight];
        const result = scoreItem.score.data?.advancedResult
            ? scoreItem.score.data?.advancedResult
            : (scoreItem.score.data?.result ?? 0);
        return acc + Number(weight) * result;
    }, 0);
    const getSumOfWeights = (scores) => scores.reduce((acc, scoreItem) => acc + Number(mapOutputWeightsToValues[scoreItem.weight]), 0);
    const getEssentialOutputsScores = (scores) => scores.reduce((acc, scoreItem) => {
        const result = scoreItem.score.data?.advancedResult
            ? scoreItem.score.data?.advancedResult
            : (scoreItem.score.data?.result ?? 0);
        return acc * (result / 100);
    }, 1);
    if (noEssentialWeight) {
        const divided = getDivided(inputSetScores);
        const divider = getSumOfWeights(inputSetScores);
        return divided / divider;
    }
    if (scoresWithEssentialWeight.length > 0 && scoresWithEssentialWeight.length !== inputSetScores.length) {
        const divided = getDivided(scoresWithoutEssentialWeight);
        const divider = getSumOfWeights(scoresWithoutEssentialWeight);
        const essentialOutputsScores = getEssentialOutputsScores(scoresWithEssentialWeight);
        return (divided / divider) * essentialOutputsScores;
    }
    if (scoresWithEssentialWeight.length === inputSetScores.length) {
        return getEssentialOutputsScores(scoresWithEssentialWeight) * 100;
    }
};
export const checkInputSetUsed = (inputSetId, insightId, optimizations, resultSets) => {
    if (insightId === null) {
        return false;
    }
    const usedInOptimization = optimizations.some(({ state }) => state.input?.insightId === insightId && state.input.insightInputSetId === inputSetId);
    const usedInResultElement = resultSets.some((resultSet) => {
        const elements = getResultElementsUsingInsight(resultSet, insightId);
        return elements.length > 0;
    });
    return usedInOptimization || usedInResultElement;
};
export const checkOutputUsed = (outputId, insightId, optimizations, resultSets) => {
    if (insightId === null) {
        return false;
    }
    const usedInOptimization = optimizations.some(({ state }) => state.input?.insightId === insightId &&
        (state.input.outputId === outputId ||
            (state.input.outputId === OUTPUT_FINAL_ID && state.input.combined)));
    const usedInResultElement = resultSets.some((resultSet) => {
        const elements = getResultElementsUsingInsight(resultSet, insightId);
        return elements.some((element) => {
            switch (element.type) {
                case ResultSetElementType.Table:
                    return element.columns.some(({ parameter }) => {
                        const id = parameter.output?.id;
                        return id !== null && id === outputId;
                    });
                case ResultSetElementType.SpaceViewer:
                    return element.sources.some(({ source }) => {
                        if (!source)
                            return false;
                        const { output } = source;
                        return isNotEmpty(output) && (output.isFinal || (output.id !== null && output.id === outputId));
                    });
                case ResultSetElementType.ProcessDataViewer:
                    return element?.sources?.some(({ source }) => {
                        if (!source)
                            return false;
                        const { output } = source;
                        return isNotEmpty(output) && (output.isFinal || (output.id !== null && output.id === outputId));
                    });
                default:
                    return false;
            }
        });
    });
    return usedInOptimization || usedInResultElement;
};
export const checkBasicScoreCalculationConditions = (output, processVariables, recipes) => {
    const { type, sourceA, sourceB, processVariable, operation, transformationOperation, timeFrame } = output;
    const isComparisonAssessment = type === InsightOutputTypes.Comparison;
    const parameters = operation?.parameters;
    const guardingConditions = [sourceA, operation];
    if (timeFrame.type !== CutType.PointInTime) {
        guardingConditions.push(transformationOperation);
    }
    if (isComparisonAssessment) {
        guardingConditions.push(sourceB);
    }
    if (processVariable?.type === InsightOutputVariableTypes.Custom) {
        const customVariableId = processVariable?.customVariableId ?? processVariable.value;
        const customVariable = recipes
            .find(({ id }) => id === processVariable?.recipeId)
            ?.state?.customVariables?.find(({ id }) => id === customVariableId);
        guardingConditions.push(isNotEmpty(customVariable) &&
            getCustomVariableStatus(customVariable ?? {}) ===
                CustomVariableValueStatus.Completed
            ? processVariable
            : null);
    }
    else {
        guardingConditions.push(processVariable);
    }
    if (!!parameters &&
        parameters.some((parameter) => isNaN(parameter.value) || !isNotEmpty(parameter.value))) {
        return false;
    }
    if (guardingConditions.some((item) => !isNotEmpty(item))) {
        return false;
    }
    if (!isTimeframeCompleted(timeFrame, processVariables)) {
        return false;
    }
    return true;
};
export const getInsightsScoresCalculationRequests = (insights, processVariables, incompleteSimulationsIds, simulations, cellModels, recipes) => {
    const scoreCalculationRequests = [];
    for (const insight of insights) {
        const filledOutputs = insight.state.outputs.filter((output) => checkBasicScoreCalculationConditions(output, processVariables, recipes));
        for (const output of filledOutputs) {
            const inputSetsThatUsedInOutput = insight.state.input.inputSets.filter((inputSet) => inputSet.inputs.some((input) => input.inputId !== null && (input.id === output.sourceA || input.id === output.sourceB)));
            for (const inputSet of inputSetsThatUsedInOutput) {
                const inputUsedAsOutputSourceA = inputSet.inputs.find(({ id, inputType }) => inputType === InsightInputType.SIMULATION && output.sourceA === id);
                const inputUsedAsOutputSourceB = inputSet.inputs.find(({ id, inputType }) => inputType === InsightInputType.SIMULATION && output.sourceB === id);
                const hasIncompleteSource = [inputUsedAsOutputSourceA, inputUsedAsOutputSourceB].some((input) => {
                    if (!input) {
                        return false;
                    }
                    const simulation = simulations?.find(({ id }) => id === input.inputId);
                    const variant = simulation?.state?.adjustments?.find(({ id }) => id === input.adjustmentId);
                    return (incompleteSimulationsIds.includes(input.inputId ?? 0) ||
                        !isSimulationVariantCompleted(simulation, cellModels, recipes, variant));
                });
                if (!hasIncompleteSource) {
                    scoreCalculationRequests.push({
                        insightId: insight.id,
                        insightData: {
                            outputId: output.id ?? 0,
                            inputSetId: inputSet.id,
                            combined: false,
                            request: insight.state,
                        },
                    });
                }
            }
        }
    }
    return scoreCalculationRequests;
};
export const checkCanDisplayScore = (output, inputSetInputs, processVariables, recipes) => {
    const basicConditionsMet = checkBasicScoreCalculationConditions(output, processVariables, recipes);
    const { type, sourceA, sourceB } = output;
    const isComparisonAssessment = type === InsightOutputTypes.Comparison;
    const sourcesToCheck = !isComparisonAssessment ? [sourceA] : [sourceA, sourceB];
    const sourceInputsSelected = sourcesToCheck.every((source) => {
        const inputSetInput = inputSetInputs.find((item) => {
            if (typeof source !== "object" || source === null) {
                return item.id === source;
            }
            else {
                return item.id === source.value;
            }
        });
        return !!inputSetInput && isNotEmpty(inputSetInput.inputId);
    });
    return basicConditionsMet && sourceInputsSelected;
};
export const reduceScoreErrors = (errors) => errors?.map(({ text }) => text).join("<br/><br/>");
export const checkScoreErrored = (outputScore) => {
    const { error, isSuccess } = { ...outputScore };
    return (!isSuccess &&
        !!error?.errors?.some(({ errorType }) => errorType !== InsightScoreErrorType.SIMULATION_EVENT_NOT_FOUND));
};
export const prepareBaseScoreItem = (outputScore, canDisplayScore = true, notAdvanced) => {
    const { data, error, isSuccess } = { ...outputScore };
    const errored = checkScoreErrored(outputScore);
    const errorMessage = reduceScoreErrors(error?.errors);
    if (!canDisplayScore || (!isSuccess && !errored)) {
        return { errorMessage };
    }
    const score = notAdvanced ? data?.result : (data?.advancedResult ?? data?.result);
    return {
        score: score ?? 0,
        errorMessage,
        errored,
        hasBiasedValue: checkScoreHasBiasedValues(outputScore),
    };
};
export const preformatInsightErrors = (errors) => {
    const propertyNameCutPart = "Request.State.";
    return errors.map((insightError) => {
        const formattedPropertyName = (insightError.propertyName ?? "")
            ?.replace(propertyNameCutPart, "")
            .split(".")
            .map((name) => name.toLowerCase())
            .join(".");
        return { ...insightError, propertyName: formattedPropertyName };
    });
};
const getSourceRecipeTimestamps = (workspaceRecipes, recipeId) => {
    const recipeSteps = flatSteps(workspaceRecipes.find(({ id }) => id === recipeId)?.state?.steps ?? []);
    return getRecipeTimestamps(recipeSteps);
};
export const getAllRecipeTimestamps = (workspaceRecipes) => {
    return workspaceRecipes.reduce((options, recipe) => {
        const steps = flatSteps(recipe?.state.steps ?? []);
        return [...options, ...getRecipeTimestamps(steps)];
    }, []);
};
export const getTimestampOptions = (workspaceRecipes, workspaceSimulations, inputs, outputState) => {
    const { type, sourceA, sourceB } = { ...outputState };
    const simulationSourceAId = inputs.find(({ id }) => id === sourceA)?.inputId;
    const sourceARecipeId = workspaceSimulations.find(({ id }) => id === simulationSourceAId)?.recipeId;
    const sourceAOptions = getSourceRecipeTimestamps(workspaceRecipes, sourceARecipeId);
    switch (type) {
        case InsightOutputTypes.Evaluation:
            if (!isNotEmpty(sourceA)) {
                return [];
            }
            return sourceAOptions;
        case InsightOutputTypes.Comparison: {
            if (!isNotEmpty(sourceA) || !isNotEmpty(sourceB)) {
                return [];
            }
            const simulationSourceBId = inputs.find(({ id }) => id === sourceB)?.inputId;
            const sourceBRecipeId = workspaceSimulations.find(({ id }) => id === simulationSourceBId)?.recipeId;
            const sourceBOptions = getSourceRecipeTimestamps(workspaceRecipes, sourceBRecipeId);
            return uniqBy([...sourceAOptions, ...sourceBOptions].filter((option, i, arr) => arr.findIndex(({ label }) => label === option.label) !== i), "label");
        }
        default:
            return [];
    }
};
export const getSelectedTimestamp = (options, cut) => {
    switch (cut?.timestampType) {
        case TimestampType.Start:
            return "Start";
        case TimestampType.End:
            return "End";
        case TimestampType.Custom:
            return options?.find(({ value }) => value?.id === cut?.timestampId)?.label;
    }
};
export const getIsTimestampFilled = (cutValues) => {
    switch (cutValues?.timestampType) {
        case TimestampType.End:
        case TimestampType.Start:
            return true;
        case TimestampType.Custom:
            return isNotEmpty(cutValues?.timestampId);
        default:
            return false;
    }
};
export const isTimeframeTimestampFilled = (timeFrame) => {
    switch (timeFrame?.type) {
        case CutType.PointInTime:
            return getIsTimestampFilled(timeFrame?.point);
        case CutType.TimeSeries:
            return getIsTimestampFilled(timeFrame?.start) && getIsTimestampFilled(timeFrame.end);
    }
};
const getCutEventParts = (processVariables, cutEvent, customVariables) => {
    if (!cutEvent) {
        return {};
    }
    let unitsLabel;
    let valueLabel;
    switch (cutEvent?.operation?.type) {
        case EventValueType.GOES_OVER:
        case EventValueType.GOES_UNDER:
        case EventValueType.REACHES: {
            const { value } = { ...cutEvent.operation };
            valueLabel = renderNumberValue(value?.value);
            unitsLabel = findDictionaryItemByValue(recipeParameterUnitsType, value?.units, getDefaultUnitsPrefix)?.label;
            break;
        }
        case EventValueType.OUT_OF_RANGE: {
            const { min, max } = { ...cutEvent.operation };
            if (!isNotEmpty(min?.value) || !isNotEmpty(max?.value)) {
                return {};
            }
            valueLabel = `${renderNumberValue(min?.value)} - ${renderNumberValue(max.value)}`;
            unitsLabel = findDictionaryItemByValue(recipeParameterUnitsType, min?.units, getDefaultUnitsPrefix)?.label;
            break;
        }
        default:
            return {};
    }
    const customVariableEntity = customVariables?.find(({ id }) => id === cutEvent?.customVariable);
    if (cutEvent.customVariable) {
        unitsLabel = parseComplexUnitToHtml(customVariableEntity?.formula?.configuredUnits?.complexValue)?.html;
    }
    const searchDirectionLabel = findDictionaryItemByValue(searchDirections, cutEvent?.searchDirection)?.label;
    const selectedVariable = cutEvent?.customVariable
        ? `{${customVariableEntity?.name}}`
        : getSelectedProcessVariable(processVariables, cutEvent?.variable)?.name;
    const operationLabel = findDictionaryItemByValue(pastFormEventValueTypes, cutEvent.operation?.type)?.label;
    return { unitsLabel, valueLabel, searchDirectionLabel, selectedVariable, operationLabel };
};
export const getCutEventLabel = (processVariables, cutEvent, customVariables) => {
    const { unitsLabel, valueLabel, searchDirectionLabel, selectedVariable, operationLabel } = getCutEventParts(processVariables, cutEvent, customVariables);
    if (!isNotEmpty(valueLabel) ||
        !isNotEmpty(unitsLabel) ||
        !isNotEmpty(searchDirectionLabel) ||
        !isNotEmpty(selectedVariable) ||
        !isNotEmpty(operationLabel)) {
        return "";
    }
    return `, ${searchDirectionLabel?.toLowerCase()} ${selectedVariable} has ${operationLabel?.toLowerCase()} ${valueLabel} ${unitsLabel}`;
};
export const isCutEventFilled = (cutEvent, processVariables) => {
    const { unitsLabel, valueLabel, searchDirectionLabel, selectedVariable, operationLabel } = getCutEventParts(processVariables, cutEvent);
    if (!isNotEmpty(valueLabel) ||
        !isNotEmpty(unitsLabel) ||
        !isNotEmpty(searchDirectionLabel) ||
        !isNotEmpty(selectedVariable) ||
        !isNotEmpty(operationLabel)) {
        return false;
    }
    return true;
};
export const getTimeframeValueWithLabel = (workspaceRecipes, workspaceSimulations, processVariables, inputs, customVariables, outputState) => {
    const { timeFrame } = { ...outputState };
    const timestampOptions = getTimestampOptions(workspaceRecipes, workspaceSimulations, inputs, outputState);
    switch (timeFrame?.type) {
        case CutType.PointInTime: {
            const { point } = timeFrame;
            const timestampLabel = getSelectedTimestamp(timestampOptions, point);
            const offsetUnits = findDictionaryItemByValue(timeUnits, point?.offsetUnit)?.label;
            let label = "";
            if (timestampLabel) {
                label = timestampLabel;
                if (isNotEmpty(point?.offset) && offsetUnits) {
                    const offsetLabel = point?.offset === 0 ? "" : ` (${renderNumberValue(point?.offset)} ${offsetUnits})`;
                    label = `${label}${offsetLabel}${getCutEventLabel(processVariables, point?.cutEvent, customVariables)}`;
                }
            }
            return {
                value: timeFrame,
                label,
            };
        }
        case CutType.TimeSeries: {
            const { start, end } = timeFrame;
            const startTimestampLabel = getSelectedTimestamp(timestampOptions, start);
            const endTimestampLabel = getSelectedTimestamp(timestampOptions, end);
            const startOffsetUnits = findDictionaryItemByValue(timeUnits, start?.offsetUnit)?.label;
            const endOffsetUnits = findDictionaryItemByValue(timeUnits, end?.offsetUnit)?.label;
            let startLabel = "";
            let endLabel = "";
            if (startTimestampLabel) {
                startLabel = startTimestampLabel;
                if (isNotEmpty(start?.offset) && startOffsetUnits) {
                    const offsetLabel = start?.offset === 0 ? "" : ` (${renderNumberValue(start?.offset)} ${startOffsetUnits})`;
                    startLabel = `${startLabel}${offsetLabel}`;
                }
                startLabel = `${startLabel}${getCutEventLabel(processVariables, start?.cutEvent, customVariables)}`;
            }
            if (endTimestampLabel) {
                endLabel = endTimestampLabel;
                if (isNotEmpty(end?.offset) && endOffsetUnits) {
                    const offsetLabel = end?.offset === 0 ? "" : ` (${renderNumberValue(end?.offset)} ${endOffsetUnits})`;
                    endLabel = `${endLabel}${offsetLabel}`;
                }
                endLabel = `${endLabel}${getCutEventLabel(processVariables, end?.cutEvent, customVariables)}`;
            }
            return {
                value: timeFrame,
                label: startLabel && endLabel ? `${startLabel} - ${endLabel}` : "",
            };
        }
        default:
            return;
    }
};
export const isTimeframeCompleted = (timeFrame, processVariables) => {
    if (!isTimeframeTimestampFilled(timeFrame)) {
        return false;
    }
    switch (timeFrame?.type) {
        case CutType.PointInTime:
            if (timeFrame.point.cutEvent && !isCutEventFilled(timeFrame.point.cutEvent, processVariables)) {
                return false;
            }
            break;
        case CutType.TimeSeries:
            if ((timeFrame.start.cutEvent && !isCutEventFilled(timeFrame.start.cutEvent, processVariables)) ||
                (timeFrame.end.cutEvent && !isCutEventFilled(timeFrame.end.cutEvent, processVariables))) {
                return false;
            }
    }
    return true;
};
export const getInsightRemoveConfirmationWarning = (optimizations, hasRelatedResults) => {
    if (optimizations.length && hasRelatedResults) {
        return DELETE_SCENARIO_ENTITY_MESSAGES.REMOVE_INSIGHT_INCLUDED_OPTIMIZATIONS_AND_RESULTS;
    }
    if (optimizations.length) {
        return DELETE_SCENARIO_ENTITY_MESSAGES.REMOVE_INSIGHT_INCLUDED_OPTIMIZATIONS;
    }
    if (hasRelatedResults) {
        return DELETE_SCENARIO_ENTITY_MESSAGES.REMOVE_INSIGHT_INCLUDED_RESULTS;
    }
};
export const getAssessmentUsedRecipeIds = (simulations, recipes, expandedInputSetInputs, outputType, sourceA, sourceB) => {
    const sourceAEntity = expandedInputSetInputs?.find((item) => item.id === sourceA);
    const sourceBEntity = expandedInputSetInputs?.find((item) => item.id === sourceB);
    const sourceASimulation = simulations?.find(({ id }) => id === Number(sourceAEntity?.inputId?.value));
    const sourceBSimulation = simulations?.find(({ id }) => id === Number(sourceBEntity?.inputId?.value));
    const sourceARecipeId = recipes?.find(({ id }) => id === sourceASimulation?.recipeId)?.id;
    const sourceBRecipeId = recipes?.find(({ id }) => id === sourceBSimulation?.recipeId)?.id;
    const usedRecipeIds = [sourceARecipeId];
    if (outputType === InsightOutputTypes.Comparison) {
        usedRecipeIds.push(sourceBRecipeId);
    }
    return usedRecipeIds;
};
export const checkInsightHasInvalidScore = (insightId, inputSetId, outputId, isFinal, persistedInsightsData) => {
    const currentInsightData = persistedInsightsData.find((persistedInsightsDataItem) => persistedInsightsDataItem.insightId === insightId);
    if (currentInsightData === undefined)
        return true;
    if (isFinal) {
        const finalScore = calculateInputSetFinalScore(insightId, inputSetId, persistedInsightsData);
        return !isNotEmpty(finalScore);
    }
    const outputScore = currentInsightData?.scores?.find((score) => score.inputSetId === inputSetId && score.outputId === outputId);
    return !outputScore || checkScoreErrored(outputScore?.score);
};
export const checkOptimizationErrored = (insightId, inputSetId, outputId, persistedInsightsData) => {
    const inputSetScores = getInsightLatestScoreData(persistedInsightsData, insightId)?.scores?.filter((score) => {
        if (outputId === OUTPUT_FINAL_ID) {
            return score.inputSetId === inputSetId;
        }
        else {
            return score.inputSetId === inputSetId && score.outputId === outputId;
        }
    });
    return inputSetScores?.some(({ score }) => checkScoreErrored(score));
};
export const isInputSetSourceCompleted = (input, incompleteSimulationsIds) => {
    const { inputId } = input;
    return (inputId &&
        !incompleteSimulationsIds.includes(typeof inputId === "object" ? inputId?.value : inputId));
};
export const isVariantUsedInInsight = (insights, simulationId, variantId) => insights.some((insight) => insight?.state?.input?.inputSets.some((inputSet) => inputSet?.inputs.some((input) => input?.inputType === InsightInputType.SIMULATION &&
    input?.inputId === simulationId &&
    input?.adjustmentId === variantId)));
